/**
 * Application state.
 *
 * One store holds the catalogue, the per-photo recipe and history, the chat
 * transcript and the view. The renderer reads from it; nothing writes pixels
 * except through a recipe change, which keeps undo, provenance and the AI's
 * proposals all operating on the same object.
 */

import { create } from 'zustand';
import {
  resetControl as resetOneControl,
  type ChatMessage,
  type CurveChannel,
  type CurvePoint,
  type DecodedImage,
  type EditPlan,
  type MaskKind,
  type MaskLayer,
  type Recipe,
  applyPlan,
  cloneRecipe,
  defaultMask,
  defaultRecipe,
  type ImageStats,
} from '@lumos/core';
import type { ViewState } from '../engine/renderer.ts';
import { decodeFile } from '../raw/decoderClient.ts';

export type PhotoStatus = 'queued' | 'decoding' | 'ready' | 'error';

export interface Photo {
  id: string;
  file: File;
  filename: string;
  status: PhotoStatus;
  progress: string;
  error?: string;
  /** Working-resolution scene-linear image. Dropped for photos far from the selection. */
  image?: DecodedImage;
  /** Small JPEG data URL for the filmstrip. */
  thumbnail?: string;
  recipe: Recipe;
  history: Recipe[];
  historyIndex: number;
  chat: ChatMessage[];
  /** Marks a photo whose recipe differs from the default. */
  edited: boolean;
}

export type PanelId =
  | 'edits'
  | 'looks'
  | 'light'
  | 'color'
  | 'curve'
  | 'detail'
  | 'effects'
  | 'geometry'
  | 'masks'
  | 'history';
export type SurfaceMode = 'guided' | 'expert';

export interface ClipboardEntry {
  recipe: Recipe;
  sourceFilename: string;
}

interface StoreState {
  photos: Photo[];
  selectedId: string | null;
  /** Additional photos selected for batch operations. Always includes selectedId. */
  selection: string[];

  view: ViewState;
  showClipping: boolean;
  showBefore: boolean;
  splitPosition: number | null;
  activeMaskIndex: number;
  showMaskOverlay: boolean;
  openPanels: Record<PanelId, boolean>;
  mode: SurfaceMode;
  /** Rail and chat widths in pixels, dragged by the user and remembered. */
  railWidth: number;
  chatWidth: number;
  chatOpen: boolean;
  /** Whether the inline explanations are showing. Off by default; the labels carry it. */
  showHelp: boolean;

  stats: ImageStats | null;
  clipboard: ClipboardEntry | null;
  /** Plan currently staged for review, keyed by photo id. */
  pendingPlan: { photoId: string; plan: EditPlan; selected: Set<number> } | null;
  chatBusy: boolean;
  toast: { message: string; tone: 'info' | 'error' } | null;

  addFiles: (files: File[]) => void;
  removePhoto: (id: string) => void;
  select: (id: string, additive?: boolean, range?: boolean) => void;
  selectAll: () => void;

  setRecipe: (recipe: Recipe, options?: { commit?: boolean }) => void;
  updateRecipe: (mutate: (draft: Recipe) => void, options?: { commit?: boolean }) => void;
  commit: () => void;
  undo: () => void;
  redo: () => void;
  resetRecipe: () => void;
  resetPanel: (panel: PanelId) => void;

  setCurve: (channel: CurveChannel, points: CurvePoint[], commit?: boolean) => void;
  addMask: (kind: MaskKind) => void;
  updateMask: (index: number, mutate: (mask: MaskLayer) => void, commit?: boolean) => void;
  removeMask: (index: number) => void;
  setActiveMask: (index: number) => void;

  copySettings: () => void;
  pasteSettings: () => void;
  syncToSelection: () => void;

  setView: (view: Partial<ViewState>) => void;
  zoomTo: (zoom: number) => void;
  toggleClipping: () => void;
  setShowBefore: (value: boolean) => void;
  setSplitPosition: (value: number | null) => void;
  togglePanel: (panel: PanelId) => void;
  setPanelOpen: (panel: PanelId, open: boolean) => void;
  setMode: (mode: SurfaceMode) => void;
  setRailWidth: (width: number) => void;
  setChatWidth: (width: number) => void;
  toggleChat: () => void;
  toggleHelp: () => void;
  /** Puts one control back to its default without disturbing the rest. */
  resetControl: (id: string) => void;
  /** Moves to a point in this photo's history. */
  jumpTo: (index: number) => void;
  clearChat: () => void;
  setShowMaskOverlay: (value: boolean) => void;

  setStats: (stats: ImageStats | null) => void;
  appendChat: (photoId: string, message: ChatMessage) => void;
  updateChat: (photoId: string, messageId: string, patch: Partial<ChatMessage>) => void;
  setChatBusy: (busy: boolean) => void;
  stagePlan: (photoId: string, plan: EditPlan) => void;
  togglePlanOperation: (index: number) => void;
  setPlanSelection: (indexes: Set<number>) => void;
  acceptPlan: () => void;
  dismissPlan: () => void;

  notify: (message: string, tone?: 'info' | 'error') => void;
  clearToast: () => void;
}

/**
 * How many photos keep their decoded pixels in memory. A 24 MP working image
 * is roughly 70 MB of float; holding a whole shoot would exhaust the tab.
 */
const RESIDENT_IMAGES = 4;

/** Longest edge of the interactive working image. */
export const WORKING_EDGE = 2560;

const HISTORY_LIMIT = 60;

const LAYOUT_KEY = 'lumos.layout';

interface Layout {
  railWidth: number;
  chatWidth: number;
  chatOpen: boolean;
  showHelp: boolean;
  mode: SurfaceMode;
}

const DEFAULT_LAYOUT: Layout = {
  railWidth: 336,
  chatWidth: 380,
  chatOpen: true,
  showHelp: false,
  mode: 'guided',
};

function readLayout(): Layout {
  try {
    const raw = window.localStorage.getItem(LAYOUT_KEY);
    if (!raw) return DEFAULT_LAYOUT;
    const parsed = JSON.parse(raw) as Partial<Layout>;
    return {
      railWidth: clampWidth(parsed.railWidth ?? DEFAULT_LAYOUT.railWidth, 260, 560),
      chatWidth: clampWidth(parsed.chatWidth ?? DEFAULT_LAYOUT.chatWidth, 300, 640),
      chatOpen: parsed.chatOpen ?? DEFAULT_LAYOUT.chatOpen,
      showHelp: parsed.showHelp ?? DEFAULT_LAYOUT.showHelp,
      mode: parsed.mode === 'expert' ? 'expert' : 'guided',
    };
  } catch {
    return DEFAULT_LAYOUT;
  }
}

function clampWidth(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, Math.round(value)));
}

/**
 * Compile-time proof that a switch covered every case. Never throws in
 * practice; a panel id that reaches it is a type error at the call site.
 */
function assertNever(value: never): void {
  console.warn('Lumos was asked to reset a panel it does not know', value);
}

/** The persistable slice of the store, so no setter has to enumerate it. */
function layoutOf(state: Pick<StoreState, keyof Layout>): Layout {
  return {
    railWidth: state.railWidth,
    chatWidth: state.chatWidth,
    chatOpen: state.chatOpen,
    showHelp: state.showHelp,
    mode: state.mode,
  };
}

/**
 * Coalesced, because `setRailWidth` and `setChatWidth` are called on every
 * pointer move of a column drag. Writing localStorage synchronously per move
 * puts a serialise-and-store on the same frame as the resize, which is exactly
 * the frame that has to stay on the hand. The state itself updates immediately;
 * only the record of it waits.
 */
let persistTimer = 0;
let pendingLayout: Layout | null = null;

function persistLayout(layout: Layout): void {
  pendingLayout = layout;
  if (persistTimer !== 0) return;
  persistTimer = window.setTimeout(() => {
    persistTimer = 0;
    const next = pendingLayout;
    pendingLayout = null;
    if (!next) return;
    try {
      window.localStorage.setItem(LAYOUT_KEY, JSON.stringify(next));
    } catch {
      // A browser with storage disabled just starts from the defaults each time.
    }
  }, 250);
}

/** A tab closed inside the debounce window must still remember its layout. */
function flushLayout(): void {
  if (persistTimer === 0 || !pendingLayout) return;
  window.clearTimeout(persistTimer);
  persistTimer = 0;
  const next = pendingLayout;
  pendingLayout = null;
  try {
    window.localStorage.setItem(LAYOUT_KEY, JSON.stringify(next));
  } catch {
    // As above: storage off means the defaults come back next time.
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushLayout);
}

/** Read once at module load, so every layout field starts from the same record. */
const startingLayout = readLayout();

let photoCounter = 0;

function newPhoto(file: File): Photo {
  photoCounter += 1;
  const recipe = defaultRecipe();
  return {
    id: `photo-${photoCounter}-${file.name}`,
    file,
    filename: file.name,
    status: 'queued',
    progress: '',
    recipe,
    history: [cloneRecipe(recipe)],
    historyIndex: 0,
    chat: [],
    edited: false,
  };
}

export const useStore = create<StoreState>()((set, get) => {
  /** Applies a change to the selected photo's recipe. */
  const mutateSelected = (
    mutate: (photo: Photo) => void,
    options: { commit?: boolean } = {},
  ) => {
    const { selectedId } = get();
    if (!selectedId) return;
    set((state) => ({
      photos: state.photos.map((photo) => {
        if (photo.id !== selectedId) return photo;
        const next = { ...photo, recipe: cloneRecipe(photo.recipe) };
        mutate(next);
        next.edited = JSON.stringify(next.recipe) !== JSON.stringify(defaultRecipe());
        if (options.commit) return pushHistory(next);
        return next;
      }),
    }));
  };

  const pushHistory = (photo: Photo): Photo => {
    const last = photo.history[photo.historyIndex];
    if (last && JSON.stringify(last) === JSON.stringify(photo.recipe)) return photo;
    const history = photo.history.slice(0, photo.historyIndex + 1);
    history.push(cloneRecipe(photo.recipe));
    const trimmed = history.length > HISTORY_LIMIT ? history.slice(history.length - HISTORY_LIMIT) : history;
    return { ...photo, history: trimmed, historyIndex: trimmed.length - 1 };
  };

  /** Frees decoded pixels for photos the user has navigated away from. */
  const evict = (photos: Photo[], keepId: string | null): Photo[] => {
    const ready = photos.filter((photo) => photo.image);
    if (ready.length <= RESIDENT_IMAGES) return photos;
    const keepIndex = photos.findIndex((photo) => photo.id === keepId);
    const keep = new Set<string>();
    for (let offset = 0; keep.size < RESIDENT_IMAGES && offset < photos.length; offset += 1) {
      for (const index of [keepIndex - offset, keepIndex + offset]) {
        if (index >= 0 && index < photos.length && photos[index].image) keep.add(photos[index].id);
      }
    }
    return photos.map((photo) =>
      photo.image && !keep.has(photo.id) ? { ...photo, image: undefined, status: 'queued' as const } : photo,
    );
  };

  const decode = async (id: string) => {
    const photo = get().photos.find((entry) => entry.id === id);
    if (!photo || photo.image || photo.status === 'decoding') return;

    set((state) => ({
      photos: state.photos.map((entry) =>
        entry.id === id ? { ...entry, status: 'decoding' as const, progress: 'Reading file', error: undefined } : entry,
      ),
    }));

    try {
      const image = await decodeFile(photo.file, photo.filename, {
        maxWorkingEdge: WORKING_EDGE,
        onProgress: (_stage, message) => {
          set((state) => ({
            photos: state.photos.map((entry) =>
              entry.id === id ? { ...entry, progress: message } : entry,
            ),
          }));
        },
      }).promise;

      set((state) => ({
        photos: evict(
          state.photos.map((entry) =>
            entry.id === id ? { ...entry, image, status: 'ready' as const, progress: '' } : entry,
          ),
          state.selectedId,
        ),
      }));
    } catch (error) {
      set((state) => ({
        photos: state.photos.map((entry) =>
          entry.id === id
            ? {
                ...entry,
                status: 'error' as const,
                progress: '',
                error: error instanceof Error ? error.message : String(error),
              }
            : entry,
        ),
      }));
    }
  };

  return {
    photos: [],
    selectedId: null,
    selection: [],
    view: { zoom: 1, panX: 0, panY: 0, mode: 'fit' },
    showClipping: false,
    showBefore: false,
    splitPosition: null,
    activeMaskIndex: -1,
    showMaskOverlay: true,
    openPanels: {
      edits: true,
      looks: true,
      light: true,
      color: true,
      curve: true,
      detail: false,
      effects: false,
      geometry: false,
      masks: false,
      history: false,
    },
    // One read, so the five layout fields cannot disagree if storage is
    // rewritten between them.
    ...startingLayout,
    stats: null,
    clipboard: null,
    pendingPlan: null,
    chatBusy: false,
    toast: null,

    addFiles: (files) => {
      if (files.length === 0) return;
      const created = files.map(newPhoto);
      set((state) => ({
        photos: [...state.photos, ...created],
        selectedId: state.selectedId ?? created[0].id,
        selection: state.selection.length > 0 ? state.selection : [created[0].id],
      }));
      // Decode the first one straight away; the rest stream in behind it.
      void (async () => {
        for (const photo of created) await decode(photo.id);
      })();
    },

    removePhoto: (id) => {
      set((state) => {
        const photos = state.photos.filter((photo) => photo.id !== id);
        const selectedId =
          state.selectedId === id ? (photos[0]?.id ?? null) : state.selectedId;
        return {
          photos,
          selectedId,
          selection: selectedId ? [selectedId] : [],
        };
      });
    },

    select: (id, additive = false, range = false) => {
      const state = get();
      if (additive) {
        const selection = state.selection.includes(id)
          ? state.selection.filter((entry) => entry !== id)
          : [...state.selection, id];
        const next = selection.length > 0 ? selection : [id];
        // Adding a photo to the selection must not move the anchor. The anchor
        // is the photo Sync copies *from*, so moving it would mean grading a
        // frame, gathering the rest of the set, and then copying the settings of
        // whichever thumbnail happened to be clicked last.
        const anchorSurvives = state.selectedId !== null && next.includes(state.selectedId);
        set({ selection: next, selectedId: anchorSurvives ? state.selectedId : next[0] });
      } else if (range && state.selectedId) {
        const from = state.photos.findIndex((photo) => photo.id === state.selectedId);
        const to = state.photos.findIndex((photo) => photo.id === id);
        const [lo, hi] = from < to ? [from, to] : [to, from];
        set({
          selection: state.photos.slice(lo, hi + 1).map((photo) => photo.id),
          selectedId: id,
        });
      } else {
        set({ selectedId: id, selection: [id], activeMaskIndex: -1, pendingPlan: null });
      }
      set((current) => ({ photos: evict(current.photos, id) }));
      void decode(id);
    },

    selectAll: () => set((state) => ({ selection: state.photos.map((photo) => photo.id) })),

    setRecipe: (recipe, options) => mutateSelected((photo) => { photo.recipe = recipe; }, options),

    updateRecipe: (mutate, options) => mutateSelected((photo) => mutate(photo.recipe), options),

    commit: () => mutateSelected(() => {}, { commit: true }),

    undo: () => {
      const { selectedId } = get();
      if (!selectedId) return;
      set((state) => ({
        photos: state.photos.map((photo) => {
          if (photo.id !== selectedId || photo.historyIndex <= 0) return photo;
          const historyIndex = photo.historyIndex - 1;
          return {
            ...photo,
            historyIndex,
            recipe: cloneRecipe(photo.history[historyIndex]),
            edited: JSON.stringify(photo.history[historyIndex]) !== JSON.stringify(defaultRecipe()),
          };
        }),
      }));
    },

    redo: () => {
      const { selectedId } = get();
      if (!selectedId) return;
      set((state) => ({
        photos: state.photos.map((photo) => {
          if (photo.id !== selectedId || photo.historyIndex >= photo.history.length - 1) return photo;
          const historyIndex = photo.historyIndex + 1;
          return {
            ...photo,
            historyIndex,
            recipe: cloneRecipe(photo.history[historyIndex]),
            edited: JSON.stringify(photo.history[historyIndex]) !== JSON.stringify(defaultRecipe()),
          };
        }),
      }));
    },

    resetRecipe: () => {
      mutateSelected((photo) => { photo.recipe = defaultRecipe(); }, { commit: true });
      set({ activeMaskIndex: -1 });
    },

    resetPanel: (panel) => {
      const fresh = defaultRecipe();
      mutateSelected((photo) => {
        const recipe = photo.recipe;
        switch (panel) {
          case 'looks': {
            // Clearing a look returns the style controls to neutral but keeps
            // the decisions that belong to the frame itself.
            const keep = {
              temperature: recipe.temperature,
              tint: recipe.tint,
              crop: recipe.crop,
              rotate: recipe.rotate,
              orientation: recipe.orientation,
              flipHorizontal: recipe.flipHorizontal,
              flipVertical: recipe.flipVertical,
              masks: recipe.masks,
              noiseLuminance: recipe.noiseLuminance,
              noiseLuminanceDetail: recipe.noiseLuminanceDetail,
              noiseColor: recipe.noiseColor,
            };
            Object.assign(recipe, defaultRecipe(), keep);
            break;
          }
          case 'light':
            Object.assign(recipe, {
              exposure: fresh.exposure,
              contrast: fresh.contrast,
              highlights: fresh.highlights,
              shadows: fresh.shadows,
              whites: fresh.whites,
              blacks: fresh.blacks,
            });
            break;
          case 'color':
            recipe.temperature = null;
            recipe.tint = null;
            recipe.vibrance = 0;
            recipe.saturation = 0;
            recipe.hsl = fresh.hsl;
            recipe.grading = fresh.grading;
            break;
          case 'curve':
            recipe.curves = fresh.curves;
            break;
          case 'detail':
            Object.assign(recipe, {
              sharpenAmount: fresh.sharpenAmount,
              sharpenRadius: fresh.sharpenRadius,
              sharpenDetail: fresh.sharpenDetail,
              sharpenMasking: fresh.sharpenMasking,
              noiseLuminance: fresh.noiseLuminance,
              noiseLuminanceDetail: fresh.noiseLuminanceDetail,
              noiseColor: fresh.noiseColor,
            });
            break;
          case 'effects':
            Object.assign(recipe, {
              texture: 0,
              clarity: 0,
              dehaze: 0,
              vignetteAmount: fresh.vignetteAmount,
              vignetteMidpoint: fresh.vignetteMidpoint,
              vignetteFeather: fresh.vignetteFeather,
              vignetteRoundness: fresh.vignetteRoundness,
              grainAmount: fresh.grainAmount,
              grainSize: fresh.grainSize,
            });
            break;
          case 'geometry':
            recipe.crop = null;
            recipe.rotate = 0;
            recipe.orientation = 0;
            recipe.flipHorizontal = false;
            recipe.flipVertical = false;
            break;
          case 'masks':
            recipe.masks = [];
            break;
          case 'edits':
          case 'history':
            // Both are views onto the recipe rather than groups of controls:
            // Edits resets one row at a time and History moves the pointer, so
            // there is nothing here for a panel-wide reset to clear. Listed so
            // the switch stays exhaustive and a new panel id fails to compile
            // until someone decides what resetting it means.
            break;
          default:
            assertNever(panel);
        }
      }, { commit: true });
    },

    setCurve: (channel, points, commit = false) =>
      mutateSelected((photo) => { photo.recipe.curves[channel] = points; }, { commit }),

    addMask: (kind) => {
      const state = get();
      const photo = state.photos.find((entry) => entry.id === state.selectedId);
      if (!photo) return;
      const index = photo.recipe.masks.length;
      const names: Record<MaskKind, string> = {
        radial: 'Radial',
        linear: 'Linear gradient',
        luminance: 'Luminance range',
        color: 'Colour range',
      };
      mutateSelected((entry) => {
        entry.recipe.masks.push(
          defaultMask(kind, `mask-${Date.now()}-${index}`, `${names[kind]} ${index + 1}`),
        );
      }, { commit: true });
      set({ activeMaskIndex: index, showMaskOverlay: true, openPanels: { ...state.openPanels, masks: true } });
    },

    updateMask: (index, mutate, commit = false) =>
      mutateSelected((photo) => {
        const mask = photo.recipe.masks[index];
        if (mask) mutate(mask);
      }, { commit }),

    removeMask: (index) => {
      mutateSelected((photo) => { photo.recipe.masks.splice(index, 1); }, { commit: true });
      set((state) => ({ activeMaskIndex: Math.min(state.activeMaskIndex, index - 1) }));
    },

    setActiveMask: (index) => set({ activeMaskIndex: index }),

    copySettings: () => {
      const state = get();
      const photo = state.photos.find((entry) => entry.id === state.selectedId);
      if (!photo) return;
      set({
        clipboard: { recipe: cloneRecipe(photo.recipe), sourceFilename: photo.filename },
        toast: { message: `Copied settings from ${photo.filename}`, tone: 'info' },
      });
    },

    pasteSettings: () => {
      const { clipboard } = get();
      if (!clipboard) return;
      mutateSelected((photo) => { photo.recipe = cloneRecipe(clipboard.recipe); }, { commit: true });
      set({ toast: { message: 'Pasted settings', tone: 'info' } });
    },

    syncToSelection: () => {
      const state = get();
      const source = state.photos.find((entry) => entry.id === state.selectedId);
      if (!source) return;
      const targets = state.selection.filter((id) => id !== source.id);
      if (targets.length === 0) {
        set({ toast: { message: 'Select more photos in the filmstrip first', tone: 'error' } });
        return;
      }
      set((current) => ({
        photos: current.photos.map((photo) => {
          if (!targets.includes(photo.id)) return photo;
          // `edited` has to be measured, not assumed: syncing an untouched
          // photo's settings across a selection should clear the marks, not set
          // them.
          const recipe = cloneRecipe(source.recipe);
          const next = {
            ...photo,
            recipe,
            edited: JSON.stringify(recipe) !== JSON.stringify(defaultRecipe()),
          };
          return pushHistory(next);
        }),
        toast: {
          message: `Applied ${source.filename} settings to ${targets.length} photo${targets.length === 1 ? '' : 's'}`,
          tone: 'info',
        },
      }));
    },

    setView: (view) => set((state) => ({ view: { ...state.view, ...view } })),

    zoomTo: (zoom) =>
      set({ view: { zoom, panX: 0, panY: 0, mode: zoom === 1 ? 'fit' : 'free' } }),

    toggleClipping: () => set((state) => ({ showClipping: !state.showClipping })),
    setShowBefore: (value) => set({ showBefore: value }),
    setSplitPosition: (value) => set({ splitPosition: value }),
    togglePanel: (panel) =>
      set((state) => ({ openPanels: { ...state.openPanels, [panel]: !state.openPanels[panel] } })),

    setPanelOpen: (panel, open) =>
      set((state) => ({ openPanels: { ...state.openPanels, [panel]: open } })),

    // Each of these writes its own field and then persists the whole layout
    // from state, so a new layout field cannot be silently dropped by whichever
    // setter forgot to list it.
    setMode: (mode) => {
      set({ mode });
      persistLayout(layoutOf(get()));
    },

    setRailWidth: (width) => {
      set({ railWidth: clampWidth(width, 260, 560) });
      persistLayout(layoutOf(get()));
    },

    setChatWidth: (width) => {
      set({ chatWidth: clampWidth(width, 300, 640) });
      persistLayout(layoutOf(get()));
    },

    toggleChat: () => {
      set((state) => ({ chatOpen: !state.chatOpen }));
      persistLayout(layoutOf(get()));
    },

    toggleHelp: () => {
      set((state) => ({ showHelp: !state.showHelp }));
      persistLayout(layoutOf(get()));
    },

    resetControl: (id) =>
      mutateSelected((photo) => {
        photo.recipe = resetOneControl(photo.recipe, id);
      }, { commit: true }),

    jumpTo: (index) => {
      const { selectedId } = get();
      if (!selectedId) return;
      set((state) => ({
        photos: state.photos.map((photo) => {
          if (photo.id !== selectedId) return photo;
          const target = Math.max(0, Math.min(photo.history.length - 1, index));
          const recipe = cloneRecipe(photo.history[target]);
          return {
            ...photo,
            historyIndex: target,
            recipe,
            edited: JSON.stringify(recipe) !== JSON.stringify(defaultRecipe()),
          };
        }),
      }));
    },

    clearChat: () => {
      const { selectedId } = get();
      if (!selectedId) return;
      set((state) => ({
        photos: state.photos.map((photo) =>
          photo.id === selectedId ? { ...photo, chat: [] } : photo,
        ),
        pendingPlan: state.pendingPlan?.photoId === selectedId ? null : state.pendingPlan,
      }));
    },
    setShowMaskOverlay: (value) => set({ showMaskOverlay: value }),

    setStats: (stats) => set({ stats }),

    appendChat: (photoId, message) =>
      set((state) => ({
        photos: state.photos.map((photo) =>
          photo.id === photoId ? { ...photo, chat: [...photo.chat, message] } : photo,
        ),
      })),

    updateChat: (photoId, messageId, patch) =>
      set((state) => ({
        photos: state.photos.map((photo) =>
          photo.id === photoId
            ? {
                ...photo,
                chat: photo.chat.map((message) =>
                  message.id === messageId ? { ...message, ...patch } : message,
                ),
              }
            : photo,
        ),
      })),

    setChatBusy: (busy) => set({ chatBusy: busy }),

    stagePlan: (photoId, plan) =>
      set({
        pendingPlan: {
          photoId,
          plan,
          selected: new Set(plan.operations.map((_, index) => index)),
        },
      }),

    togglePlanOperation: (index) =>
      set((state) => {
        if (!state.pendingPlan) return {};
        const selected = new Set(state.pendingPlan.selected);
        if (selected.has(index)) selected.delete(index);
        else selected.add(index);
        return { pendingPlan: { ...state.pendingPlan, selected } };
      }),

    setPlanSelection: (indexes) =>
      set((state) =>
        state.pendingPlan ? { pendingPlan: { ...state.pendingPlan, selected: indexes } } : {},
      ),

    acceptPlan: () => {
      const state = get();
      const pending = state.pendingPlan;
      if (!pending) return;
      const photo = state.photos.find((entry) => entry.id === pending.photoId);
      if (!photo) return;

      const next = applyPlan(photo.recipe, pending.plan, pending.selected);
      const partial = pending.selected.size < pending.plan.operations.length;

      set((current) => ({
        photos: current.photos.map((entry) => {
          if (entry.id !== pending.photoId) return entry;
          const updated = pushHistory({
            ...entry,
            recipe: next,
            edited: JSON.stringify(next) !== JSON.stringify(defaultRecipe()),
          });
          return {
            ...updated,
            chat: updated.chat.map((message) =>
              message.plan === pending.plan
                ? { ...message, disposition: partial ? ('partial' as const) : ('applied' as const) }
                : message,
            ),
          };
        }),
        pendingPlan: null,
        toast: {
          message: `Applied ${pending.selected.size} of ${pending.plan.operations.length} proposed changes`,
          tone: 'info',
        },
      }));
    },

    dismissPlan: () => {
      const state = get();
      const pending = state.pendingPlan;
      if (!pending) return;
      set((current) => ({
        photos: current.photos.map((entry) =>
          entry.id === pending.photoId
            ? {
                ...entry,
                chat: entry.chat.map((message) =>
                  message.plan === pending.plan ? { ...message, disposition: 'dismissed' as const } : message,
                ),
              }
            : entry,
        ),
        pendingPlan: null,
      }));
    },

    notify: (message, tone = 'info') => set({ toast: { message, tone } }),
    clearToast: () => set({ toast: null }),
  };
});

/** Convenience selector for the photo under the cursor. */
export function useSelectedPhoto(): Photo | null {
  return useStore((state) => state.photos.find((photo) => photo.id === state.selectedId) ?? null);
}
